import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Save, Loader2, AlertCircle, MapPin, Search, Crown, KeyRound,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Access master — Super-Admin-only.
 *
 * Two scopes per admin user:
 *   1. **Geofence visibility** (Phase 2)  — `admin_geofence_ids`.
 *   2. **Action permissions** (Phase 3)   — `admin_permissions`.
 *
 * Both are stored on the user document and consulted on every privileged
 * route (see `utils/admin_perms.py::require_permission`). The Super Admin
 * (`+91 9894021299`) is always shown but read-only: they see everything
 * and have every right by design.
 */
export default function AdminAccessMaster() {
  const [admins, setAdmins] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [catalogue, setCatalogue] = useState([]); // [{code,label}]
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');

  // Local edit buffers, keyed by admin.id. Keep geofences and permissions
  // independent so they can be saved separately (avoids forcing a re-fetch
  // of one when the other is dirty).
  const [editGeo, setEditGeo] = useState({});   // { admin_id -> Set<geo_id> }
  const [editPerm, setEditPerm] = useState({}); // { admin_id -> Set<perm_code> }

  const fetchAll = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('token');
      const [aRes, gRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/admin-users`,             { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/geofences`,                     { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/permissions/catalogue`,   { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (aRes.status === 403) { setErr('This panel is visible to the Super Admin only.'); return; }
      if (!aRes.ok) { setErr(`Failed to load admin list (${aRes.status})`); return; }
      const adminsList = await aRes.json();
      const geofencesList = gRes.ok ? await gRes.json() : [];
      const cat = cRes.ok ? ((await cRes.json()).permissions || []) : [];
      setAdmins(adminsList);
      setGeofences(geofencesList);
      setCatalogue(cat);
      // Seed both edit buffers from server state.
      const gBuf = {};
      const pBuf = {};
      for (const a of adminsList) {
        gBuf[a.id] = new Set(a.admin_geofence_ids || []);
        pBuf[a.id] = new Set(a.admin_permissions || []);
      }
      setEditGeo(gBuf);
      setEditPerm(pBuf);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- dirty checks ------------------------------------------------------
  const setsEqual = (cur, orig) => {
    if (cur.size !== orig.size) return false;
    for (const id of cur) if (!orig.has(id)) return false;
    return true;
  };
  const isGeoDirty  = (a) => !setsEqual(editGeo[a.id]  || new Set(), new Set(a.admin_geofence_ids || []));
  const isPermDirty = (a) => !setsEqual(editPerm[a.id] || new Set(), new Set(a.admin_permissions || []));

  // ---- geofence handlers (unchanged behaviour) ---------------------------
  const toggleGeo = (adminId, geoId) => {
    setEditGeo(prev => {
      const next = new Set(prev[adminId] || []);
      if (next.has(geoId)) next.delete(geoId); else next.add(geoId);
      return { ...prev, [adminId]: next };
    });
  };
  const setAllGeo = (adminId, on) => {
    setEditGeo(prev => ({
      ...prev,
      [adminId]: on ? new Set(geofences.map(g => g.id)) : new Set(),
    }));
  };

  // ---- permission handlers ----------------------------------------------
  const togglePerm = (adminId, code) => {
    setEditPerm(prev => {
      const next = new Set(prev[adminId] || []);
      if (next.has(code)) next.delete(code); else next.add(code);
      return { ...prev, [adminId]: next };
    });
  };
  const setAllPerms = (adminId, on) => {
    setEditPerm(prev => ({
      ...prev,
      [adminId]: on ? new Set(catalogue.map(p => p.code)) : new Set(),
    }));
  };

  // ---- save dispatchers --------------------------------------------------
  const saveGeo = async (admin) => {
    setSavingId(`${admin.id}:geo`); setMsg(''); setErr('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/admin-users/${admin.id}/geofences`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ geo_fence_ids: Array.from(editGeo[admin.id] || []) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(data.detail || `Save failed (${r.status})`); return; }
      setMsg(`Saved geofences for ${admin.name}.`);
      fetchAll();
    } finally { setSavingId(null); }
  };

  const savePerms = async (admin) => {
    setSavingId(`${admin.id}:perm`); setMsg(''); setErr('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/admin-users/${admin.id}/permissions`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: Array.from(editPerm[admin.id] || []) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(data.detail || `Save failed (${r.status})`); return; }
      setMsg(`Saved rights for ${admin.name}.`);
      fetchAll();
    } finally { setSavingId(null); }
  };

  const filtered = admins.filter(a => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (a.name || '').toLowerCase().includes(q)
        || (a.mobile || '').toLowerCase().includes(q)
        || (a.staff_id || '').toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-access-master">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={20} className="text-indigo-600" /> Admin Access &amp; Rights
          </h1>
          <p className="text-xs text-gray-500 max-w-2xl">
            Each admin gets two scopes: a <b>Geofence Scope</b> (whose data they can see)
            and a set of <b>Action Rights</b> (what they can actually do). Both default
            to empty for new admins — grant explicitly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search admin"
              className="pl-7 pr-3 py-1.5 border rounded text-sm w-56"
              data-testid="admin-access-search"
            />
          </div>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3 flex items-center gap-2" data-testid="admin-access-err">
          <AlertCircle size={14} /> {err}
        </div>
      )}
      {msg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded-lg text-sm mb-3" data-testid="admin-access-msg">
          {msg}
        </div>
      )}

      {loading ? (
        <div className="bg-white border rounded-xl p-10 text-center text-gray-500">
          <Loader2 className="inline animate-spin mr-2" size={16} /> Loading admins…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border rounded-xl p-10 text-center text-gray-500">
          {admins.length === 0 ? 'No admin users found.' : 'No admin matches your search.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(a => {
            const isSA = a.is_super_admin;
            const selGeo  = editGeo[a.id]  || new Set();
            const selPerm = editPerm[a.id] || new Set();
            const geoDirty  = isGeoDirty(a);
            const permDirty = isPermDirty(a);
            return (
              <div
                key={a.id}
                className="bg-white border rounded-xl p-4 shadow-sm"
                data-testid={`admin-card-${a.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {isSA && <Crown size={14} className="text-amber-500" title="Super Admin" />}
                      {a.name}
                      <span className="ml-1 text-xs font-mono text-gray-400">{a.staff_id || a.id}</span>
                      {!isSA && (
                        <>
                          <span
                            className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                            title="Geofence scope"
                          >
                            <MapPin size={9} className="inline mr-0.5" />
                            {selGeo.size}/{geofences.length}
                          </span>
                          <span
                            className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full border ${
                              selPerm.size === 0
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : selPerm.size === catalogue.length
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                            title="Action rights"
                          >
                            <KeyRound size={9} className="inline mr-0.5" />
                            {selPerm.size}/{catalogue.length}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{a.mobile}</div>
                  </div>
                  {isSA && (
                    <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                      Super Admin — sees all geofences &amp; has every right (always)
                    </span>
                  )}
                </div>

                {!isSA && (
                  <>
                    {/* ----- Geofence Scope ----- */}
                    <div className="border-t pt-3 mt-1">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <h3 className="text-xs uppercase tracking-wide font-semibold text-gray-600 flex items-center gap-1.5">
                          <MapPin size={12} className="text-indigo-600" /> Geofence Scope
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAllGeo(a.id, true)}
                            className="text-[11px] text-gray-600 hover:text-gray-900 underline"
                            data-testid={`select-all-geo-${a.id}`}
                          >Select all</button>
                          <button
                            type="button"
                            onClick={() => setAllGeo(a.id, false)}
                            className="text-[11px] text-gray-600 hover:text-gray-900 underline"
                            data-testid={`clear-all-geo-${a.id}`}
                          >Clear</button>
                          <button
                            type="button"
                            onClick={() => saveGeo(a)}
                            disabled={!geoDirty || savingId === `${a.id}:geo`}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs text-white ${
                              geoDirty ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'
                            }`}
                            data-testid={`save-geo-${a.id}`}
                          >
                            {savingId === `${a.id}:geo`
                              ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
                              : <><Save size={12} /> {geoDirty ? 'Save geofences' : 'Saved'}</>}
                          </button>
                        </div>
                      </div>
                      {geofences.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No geofences exist yet — add some in Geo-Fences first.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {geofences.map(g => {
                            const on = selGeo.has(g.id);
                            return (
                              <label
                                key={g.id}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-sm select-none transition ${
                                  on
                                    ? 'bg-indigo-50 border-indigo-300 text-indigo-800'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                }`}
                                data-testid={`geo-${a.id}-${g.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => toggleGeo(a.id, g.id)}
                                  className="accent-indigo-600"
                                />
                                <MapPin size={12} />
                                {g.name}
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {selGeo.size === 0 && (
                        <p className="text-[11px] text-orange-600 mt-2 italic">
                          ⚠ This admin currently has <strong>no geofences</strong> — they will not see any staff data until at least one is selected.
                        </p>
                      )}
                    </div>

                    {/* ----- Action Rights ----- */}
                    <div className="border-t pt-3 mt-3">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <h3 className="text-xs uppercase tracking-wide font-semibold text-gray-600 flex items-center gap-1.5">
                          <KeyRound size={12} className="text-emerald-600" /> Action Rights
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAllPerms(a.id, true)}
                            className="text-[11px] text-gray-600 hover:text-gray-900 underline"
                            data-testid={`select-all-perm-${a.id}`}
                          >Grant all</button>
                          <button
                            type="button"
                            onClick={() => setAllPerms(a.id, false)}
                            className="text-[11px] text-gray-600 hover:text-gray-900 underline"
                            data-testid={`clear-all-perm-${a.id}`}
                          >Revoke all</button>
                          <button
                            type="button"
                            onClick={() => savePerms(a)}
                            disabled={!permDirty || savingId === `${a.id}:perm`}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs text-white ${
                              permDirty ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'
                            }`}
                            data-testid={`save-perm-${a.id}`}
                          >
                            {savingId === `${a.id}:perm`
                              ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
                              : <><Save size={12} /> {permDirty ? 'Save rights' : 'Saved'}</>}
                          </button>
                        </div>
                      </div>
                      {catalogue.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Permission catalogue not loaded.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {catalogue.map(p => {
                            const on = selPerm.has(p.code);
                            return (
                              <label
                                key={p.code}
                                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-sm cursor-pointer select-none transition ${
                                  on
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                }`}
                                data-testid={`perm-${a.id}-${p.code}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => togglePerm(a.id, p.code)}
                                  className="accent-emerald-600 shrink-0"
                                />
                                <span className="truncate">{p.label}</span>
                                <span className="ml-auto text-[10px] text-gray-400 font-mono shrink-0 hidden sm:inline">{p.code}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {selPerm.size === 0 && (
                        <p className="text-[11px] text-red-700 mt-2 italic">
                          ⚠ This admin has <strong>no action rights</strong> — they can log in but every approval / edit
                          action will return 403 until at least one right is granted.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
